import { GanttStatic } from '../../../../assets/js/codebase/dhtmlxgantt';
import { GanttColumn } from './gantt.types';
import { formatGanttColumnValue } from './gantt-column-values';
import { customCoerceBooleanProperty } from './gantt-adapter';
import { getColumnPropertyKey } from './gantt-remember';

type FilterArea = 'task' | 'resource';

/** Local display filters. The widget retains every task, booking and dependency. */
export class GanttFloatingFilters {
    private values = { task: new Map<string, string>(), resource: new Map<string, string>() };
    private visible: { task?: Set<string>; resource?: Set<string> } = {};
    private stores = new WeakSet<object>();
    private timer?: ReturnType<typeof setTimeout>;
    private destroyed = false;
    private focus?: { area: FilterArea; key: string; start: number | null; end: number | null };

    constructor(private gantt: GanttStatic, private columns: (area: FilterArea) => GanttColumn[], private changed: () => void) {
        gantt.attachEvent('onBeforeTaskDisplay', id => this.visible.task?.has(String(id)) ?? true);
        gantt.attachEvent('onBeforeSplitTaskDisplay', id => this.visible.task?.has(String(id)) ?? true);
        gantt.attachEvent('onBeforeGanttRender', () => {
            const input = document.activeElement;
            this.focus = input instanceof HTMLInputElement && input.classList.contains('tt-gantt-floating-filter') && gantt.$container.contains(input)
                ? { area: input.dataset['area'] as FilterArea, key: input.dataset['column']!, start: input.selectionStart, end: input.selectionEnd } : undefined;
            return true;
        });
        for (const event of ['onAfterTaskAdd', 'onAfterTaskUpdate', 'onAfterTaskDelete'] as const) {
            gantt.attachEvent(event, () => {
                if (this.values.task.size) this.schedule();
            });
        }
    }

    bindResources(store: ReturnType<GanttStatic['getDatastore']>): void {
        if (this.stores.has(store)) return;
        this.stores.add(store);
        store.attachEvent('onFilterItem', id => this.isResourceVisible(id));
    }

    isResourceVisible(id: string | number): boolean {
        return this.visible.resource?.has(String(id)) ?? true;
    }

    refresh(): void {
        if (this.destroyed) return;
        if (this.timer !== undefined) clearTimeout(this.timer);
        this.timer = undefined;
        if (!this.values.task.size && !this.values.resource.size && !this.visible.task && !this.visible.resource) return;
        this.visible.task = this.matchingRows('task', this.gantt.getDatastore('task'));
        const resources = this.gantt.getDatastore(this.gantt.config.resource_store);
        this.visible.resource = resources ? this.matchingRows('resource', resources) : undefined;
        this.changed();
        this.gantt.refreshData();
        resources?.filter();
    }

    render(): void {
        for (const area of ['task', 'resource'] as const) {
            const columns = this.columns(area);
            const enabled = columns.some(column => customCoerceBooleanProperty(column.floatingFilter));
            const grid: HTMLElement | undefined = area === 'task' ? this.gantt.$grid_scale : this.gantt.getLayoutView('resourceGrid')?.$grid_scale;
            grid?.classList.toggle('tt-gantt-floating-scale', enabled);
            if (grid && enabled) {
                const timeline: HTMLElement | undefined = area === 'task' ? this.gantt.$task_scale : this.gantt.getLayoutView('resourceHistogram')?.$task;
                const lines = timeline?.querySelectorAll<HTMLElement>('.gantt_scale_line');
                const height = grid.querySelector<HTMLElement>('.gantt_grid_head_cell')?.getBoundingClientRect().height ?? grid.clientHeight;
                // Continue the timeline's lowest header divider across the filter band.
                const divider = lines && lines.length > 1
                    ? lines[lines.length - 1].getBoundingClientRect().top - grid.getBoundingClientRect().top : height - 24;
                const labelHeight = Math.max(16, Math.min(height - 20, divider));
                grid.style.setProperty('--tt-gantt-floating-label-height', labelHeight + 'px');
            } else {
                grid?.style.removeProperty('--tt-gantt-floating-label-height');
            }
            for (const header of Array.from(grid?.querySelectorAll<HTMLElement>('.gantt_grid_head_cell') ?? [])) {
                const column = columns.find(item => getColumnPropertyKey(item) === header.dataset['columnName']);
                header.classList.toggle('tt-gantt-floating-header', enabled);
                if (!enabled) continue;
                let label = header.querySelector<HTMLElement>('.tt-gantt-header-label');
                if (!label) {
                    label = document.createElement('span');
                    label.className = 'tt-gantt-header-label';
                    header.appendChild(label);
                }
                for (const child of Array.from(header.childNodes)) {
                    if (child !== label && !(child instanceof Element && child.classList.contains('tt-gantt-floating-filter'))) label.appendChild(child);
                }
                label.style.justifyContent = header.style.justifyContent;
                if (!column || !customCoerceBooleanProperty(column.floatingFilter)) continue;
                const key = getColumnPropertyKey(column);
                let input = header.querySelector<HTMLInputElement>('.tt-gantt-floating-filter');
                if (!input) {
                    input = document.createElement('input');
                    input.type = 'search';
                    input.className = 'tt-gantt-floating-filter';
                    input.dataset['area'] = area;
                    input.dataset['column'] = key;
                    input.addEventListener('input', () => {
                        const value = input!.value;
                        if (value.trim()) this.values[area].set(key, value);
                        else this.values[area].delete(key);
                        this.schedule();
                    });
                    for (const event of ['click', 'dblclick', 'pointerdown', 'mousedown', 'keydown']) input.addEventListener(event, event => event.stopPropagation());
                    input.addEventListener('keydown', event => {
                        if (event.key === 'Escape') {
                            input!.value = '';
                            this.values[area].delete(key);
                            this.refresh();
                        }
                    });
                    header.appendChild(input);
                }
                input.setAttribute('aria-label', column.label ?? key);
                const value = this.values[area].get(key) ?? '';
                if (input.value !== value) input.value = value;
                if (this.focus?.area === area && this.focus.key === key) {
                    input.focus({ preventScroll: true });
                    input.setSelectionRange(this.focus.start, this.focus.end);
                }
            }
        }
        this.focus = undefined;
    }

    destroy(): void {
        this.destroyed = true;
        if (this.timer !== undefined) clearTimeout(this.timer);
    }

    private schedule(): void {
        if (this.destroyed) return;
        if (this.timer !== undefined) clearTimeout(this.timer);
        this.timer = setTimeout(() => this.refresh(), 150);
    }

    private matchingRows(area: FilterArea, store: ReturnType<GanttStatic['getDatastore']>): Set<string> | undefined {
        const filters = this.columns(area).flatMap(column => {
            const query = this.values[area].get(getColumnPropertyKey(column))?.trim().toLocaleLowerCase();
            return query && customCoerceBooleanProperty(column.floatingFilter) ? [{ column, query }] : [];
        });
        if (!filters.length) return undefined;
        const parents = new Map<string, string>();
        const children = new Map<string, string[]>();
        const matches = new Set<string>();
        store.eachItem((row: Record<string, unknown>) => {
            const id = String(row['id']), parent = String(row['parent'] ?? '');
            parents.set(id, parent);
            const siblings = children.get(parent) ?? [];
            siblings.push(id);
            children.set(parent, siblings);
            if (filters.every(({ column, query }) => {
                const value = row[getColumnPropertyKey(column)];
                if (value == null) return false;
                return formatGanttColumnValue(value, column).toLocaleLowerCase().includes(query) || String(value).toLocaleLowerCase().includes(query);
            })) matches.add(id);
        });
        const visible = new Set(matches);
        const expanded = new Set<string>();
        for (const id of matches) {
            let parent = parents.get(id);
            while (parent && parents.has(parent) && !visible.has(parent)) {
                visible.add(parent);
                parent = parents.get(parent);
            }
            // Matching group rows keep their children; child matches keep their ancestors.
            const pending = [...(children.get(id) ?? [])];
            while (pending.length) {
                const child = pending.pop()!;
                if (expanded.has(child)) continue;
                expanded.add(child);
                visible.add(child);
                pending.push(...(children.get(child) ?? []));
            }
        }
        return visible;
    }
}
